import React, { useState } from "react";
import { Plus, Redo2, Trash2, Undo2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { SheetGrid } from "./SheetGrid";
import { ScratchpadMobileGrid } from "./ScratchpadMobileGrid";
import { SupplierExportToolbar } from "./SupplierExportToolbar";
import { SCRATCHPAD_SECTIONS, newScratchpadRow, normalizeScratchpadRow, scratchpadColumnsFor } from "./scratchpadModel";
import { SectionDispatchBadge } from "./SectionDispatchBadge";

export const MasterProductionInput = ({ sheet, sections, onSectionsChange, canEdit, onUndo, onRedo, suppliers, setSuppliers, onSheetUpdate, dirty }) => {
  const [section, setSection] = useState("cabinet_boxes");
  const [selected, setSelected] = useState(new Set());
  const rows = sections[section] || [];

  const updateRows = (nextRows) => onSectionsChange({ ...sections, [section]: nextRows });
  const addRow = () => updateRows([...rows, newScratchpadRow(section)]);
  const deleteSelected = () => {
    const next = Object.fromEntries(SCRATCHPAD_SECTIONS.map(item => [
      item.key,
      (sections[item.key] || []).filter(row => !selected.has(row.row_id)),
    ]));
    onSectionsChange(next);
    setSelected(new Set());
  };
  const toggleRow = (id, checked) => setSelected(previous => {
    const next = new Set(previous);
    checked ? next.add(id) : next.delete(id);
    return next;
  });
  const toggleAll = (checked) => setSelected(previous => {
    const next = new Set(previous);
    rows.forEach(row => checked ? next.add(row.row_id) : next.delete(row.row_id));
    return next;
  });

  return (
    <div className="space-y-3" data-testid="master-production-input">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200">
        <div className="flex min-w-0 flex-wrap gap-1 md:flex-nowrap md:overflow-x-auto" role="tablist" aria-label="Master production tables">
          {SCRATCHPAD_SECTIONS.map(item => (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={section === item.key}
              onClick={() => setSection(item.key)}
              className={`flex shrink-0 flex-col items-start gap-1 border-b-2 px-3 py-2 text-sm font-semibold transition-colors ${section === item.key ? "border-amber-500 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}
              data-testid={`master-section-${item.key}`}
            >
              <span>{item.label} <span className="ml-1 font-mono text-xs text-slate-400">{(sections[item.key] || []).length}</span></span>
              <SectionDispatchBadge sectionId={["gables_panels", "crown_moulding", "light_valance", "riser_fascia"].includes(item.key) ? "gables_mouldings" : item.key} status={sheet.shop_floor_section_status} compact testIdSuffix={item.key} />
            </button>
          ))}
        </div>
        {canEdit && (
          <div className="flex items-center gap-1 pb-1">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onUndo} title="Undo" data-testid="scratchpad-undo-button"><Undo2 className="h-4 w-4" /></Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onRedo} title="Redo" data-testid="scratchpad-redo-button"><Redo2 className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" className="h-8" onClick={addRow} data-testid="scratchpad-add-row-button"><Plus className="mr-1 h-4 w-4" /> Blank row</Button>
            <Button variant="ghost" size="sm" className="hidden h-8 text-red-600 hover:bg-red-50 hover:text-red-700 md:inline-flex" disabled={!selected.size} onClick={deleteSelected} data-testid="scratchpad-delete-rows-button"><Trash2 className="mr-1 h-4 w-4" />{selected.size || ""}</Button>
          </div>
        )}
      </div>
      <SupplierExportToolbar
        sheet={sheet}
        suppliers={suppliers}
        setSuppliers={setSuppliers}
        selectedIds={selected}
        canDispatch={canEdit}
        dirty={dirty}
        onDispatched={updated => { onSheetUpdate(updated); setSelected(new Set()); }}
      />
      <div className="hidden md:block">
        <SheetGrid
          key={section}
          section={section}
          columns={scratchpadColumnsFor(section)}
          rows={rows}
          onRowsChange={updateRows}
          onUndo={onUndo}
          onRedo={onRedo}
          selectedIds={selected}
          onToggleRow={toggleRow}
          onToggleAll={toggleAll}
          readOnly={!canEdit}
          normalizeRow={normalizeScratchpadRow}
          createRow={newScratchpadRow}
        />
      </div>
      <div className="md:hidden">
        <ScratchpadMobileGrid section={section} rows={rows} onRowsChange={updateRows} readOnly={!canEdit} selectedIds={selected} onToggleRow={toggleRow} />
      </div>
      <p className="text-xs text-slate-500" data-testid="master-routing-note">
        Wood locks to 2-face Stain, MDF defaults to 1-face Paint, and Thermofoil bypasses finishing. Paint/Stain trim rows route by linear foot.
      </p>
    </div>
  );
};